/**
 * QR encoding, client-side and synchronous.
 *
 * The payload is short text and encoding it on-device is what lets the app work
 * on a boat with no signal. Nothing here touches the network.
 */

import qrcode from "qrcode-generator";

/** A square matrix of dark/light modules, plus its size. */
export interface QrMatrix {
  size: number;
  /** `dark[row][col]`. */
  dark: boolean[][];
}

/**
 * Error correction level M is the right default: L saves a version or two but a
 * printed slate lives in a wet BCD pocket, and a scuffed code that still scans
 * is worth more than a slightly smaller one.
 */
export function encode(payload: string, ec: "L" | "M" | "Q" | "H" = "M"): QrMatrix {
  const qr = qrcode(0, ec); // 0 = pick the smallest version that fits
  qr.addData(payload);
  qr.make();

  const size = qr.getModuleCount();
  const dark: boolean[][] = [];
  for (let row = 0; row < size; row++) {
    const line: boolean[] = [];
    for (let col = 0; col < size; col++) line.push(qr.isDark(row, col));
    dark.push(line);
  }
  return { size, dark };
}

/** Standalone SVG, for on-screen display and for the "save SVG" button. */
export function toSvg(matrix: QrMatrix, opts: { cellPx?: number; quietZone?: number } = {}): string {
  const cell = opts.cellPx ?? 4;
  // The spec calls for 4 modules of quiet zone. Cameras are forgiving; wet
  // plastic and low contrast are not, so we keep the full margin.
  const quiet = opts.quietZone ?? 4;
  const dim = (matrix.size + quiet * 2) * cell;

  const rects: string[] = [];
  for (let row = 0; row < matrix.size; row++) {
    // Merge horizontal runs into one rect. A version-6 code drops from ~1,700
    // elements to a few hundred, which matters when six of them are on screen.
    let run = 0;
    for (let col = 0; col <= matrix.size; col++) {
      const on = col < matrix.size && matrix.dark[row]![col]!;
      if (on) {
        run++;
        continue;
      }
      if (run > 0) {
        const x = (quiet + col - run) * cell;
        const y = (quiet + row) * cell;
        rects.push(`<rect x="${x}" y="${y}" width="${run * cell}" height="${cell}"/>`);
        run = 0;
      }
    }
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dim} ${dim}" ` +
    `width="${dim}" height="${dim}" shape-rendering="crispEdges">` +
    `<rect width="${dim}" height="${dim}" fill="#fff"/>` +
    `<g fill="#000">${rects.join("")}</g>` +
    `</svg>`
  );
}

/** Saves a payload as a standalone SVG file, for any QR code the app renders. */
export function downloadSvg(payload: string, filename = "dive-qr.svg"): void {
  const url = URL.createObjectURL(new Blob([toSvg(encode(payload))], { type: "image/svg+xml" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Horizontal runs of dark modules, as [row, startCol, length]. The PDF writer
 *  wants these directly: one filled rectangle per run instead of per module. */
export function darkRuns(matrix: QrMatrix): [number, number, number][] {
  const runs: [number, number, number][] = [];
  for (let row = 0; row < matrix.size; row++) {
    let run = 0;
    for (let col = 0; col <= matrix.size; col++) {
      const on = col < matrix.size && matrix.dark[row]![col]!;
      if (on) {
        run++;
      } else if (run > 0) {
        runs.push([row, col - run, run]);
        run = 0;
      }
    }
  }
  return runs;
}
